import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../config";
import "./OnboardingPage.css";
import ReportManager from "./ReportManager";
import { checkSubscriptionLimits } from "../utils/subscriptionUtils";
import { MdClose, MdComputer, MdFolder, MdEdit, MdAdd, MdDelete , MdCalendarToday ,MdWork , MdVisibility, MdRefresh  } from "react-icons/md";
import { FiFolder, FiFile, FiEdit, FiInfo,  FiCheckCircle,  FiAlertTriangle,  FiStar ,FiDownload , FiSend , FiLock, FiUsers , FiHome  } from "react-icons/fi";

// Modal Components for Employee Dashboard Features
const EmployeeQuickViewModal = ({ employee, isOpen, onClose }) => {
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Check if allocated_items exists and is an array
  const allocatedItems = Array.isArray(employee.allocated_items)
    ? employee.allocated_items
    : employee.allocated_item
      ? [employee.allocated_item]
      : [];

  return (
    <div className="onbr-modal-overlay" onClick={handleOverlayClick}>
      <div className="onbr-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="onbr-modal-header">
          <h3>Employee Quick View</h3>
          <button className="onbr-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="onbr-modal-body">
          <div className="onbr-employee-quick-view">
            <div className="onbr-employee-avatar-large">
              {employee.name?.charAt(0).toUpperCase() || 'E'}
            </div>
            <div className="onbr-employee-details-grid">
              <div className="onbr-detail-item">
                <label>Name</label>
                <span>{employee.name}</span>
              </div>
              <div className="onbr-detail-item">
                <label>Employee ID</label>
                <span>{employee.emp_id || 'N/A'}</span>
              </div>
              <div className="onbr-detail-item">
                <label>Department</label>
                <span>{employee.department}</span>
              </div>
              <div className="onbr-detail-item">
                <label>Role</label>
                <span>{employee.role}</span>
              </div>
              <div className="onbr-detail-item">
                <label>Email</label>
                <span>{employee.email}</span>
              </div>
              <div className="onbr-detail-item">
                <label>Mobile</label>
                <span>{employee.mobile}</span>
              </div>
              <div className="onbr-detail-item">
                <label>Allocated Items</label>
                <div className="onbr-items-tags">
                  {allocatedItems.length > 0 ? (
                    allocatedItems.map((item, index) => (
                      <span key={index} className="onbr-item-tag">{item}</span>
                    ))
                  ) : (
                    <span className="onbr-no-items">No items allocated</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="onbr-modal-footer">
          <button className="onbr-btn-secondary" onClick={onClose}>Close</button>
          <button className="onbr-btn-primary">View Full Profile</button>
        </div>
      </div>
    </div>
  );
};

const DepartmentManagementModal = ({ isOpen, onClose, departments, onAdd, onEdit, onDelete }) => {
  const [newDeptName, setNewDeptName] = useState("");
  const [editingDept, setEditingDept] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingDept) {
      onEdit(editingDept._id, newDeptName);
    } else {
      onAdd(newDeptName);
    }
    setNewDeptName("");
    setEditingDept(null);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="onbr-modal-overlay" onClick={handleOverlayClick}>
      <div className="onbr-modal-content onbr-large" onClick={(e) => e.stopPropagation()}>
        <div className="onbr-modal-header">
          <h3>Department Management</h3>
          <button className="onbr-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="onbr-modal-body">
          <form onSubmit={handleSubmit} className="onbr-department-form">
            <div className="onbr-form-group">
              <input
                type="text"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                placeholder="Enter department name"
                required
              />
            </div>
            <button type="submit" className="onbr-btn-primary">
              {editingDept ? 'Update Department' : 'Add Department'}
            </button>
          </form>

          <div className="onbr-departments-list">
            <h4>Existing Departments</h4>
            {departments.map(dept => (
              <div key={dept._id} className="onbr-department-item">
                <span>{dept.name}</span>
                <div className="onbr-department-actions">
                  <button
                    className="onbr-btn-edit"
                    onClick={() => {
                      setEditingDept(dept);
                      setNewDeptName(dept.name);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="onbr-btn-danger"
                    onClick={() => onDelete(dept._id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const AllocationManagementModal = ({ isOpen, onClose, items, onAdd, onEdit, onDelete }) => {
  const [newItemName, setNewItemName] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [categories, setCategories] = useState([
    { id: 1, name: "Electronics" },
    { id: 2, name: "Furniture" },
    { id: 3, name: "Stationery" },
    { id: 4, name: "Tools" },
    { id: 5, name: "Safety Equipment" },
  ]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [activeTab, setActiveTab] = useState("items"); // "items" or "categories"

  const handleItemSubmit = (e) => {
    e.preventDefault();
    if (editingItem) {
      onEdit(editingItem._id, newItemName);
    } else {
      onAdd(newItemName);
    }
    setNewItemName("");
    setEditingItem(null);
    setSelectedCategory("");
  };

  const handleCategorySubmit = (e) => {
    e.preventDefault();
    if (newCategoryName.trim()) {
      const newCategory = {
        id: categories.length + 1,
        name: newCategoryName.trim()
      };
      setCategories([...categories, newCategory]);
      setNewCategoryName("");
    }
  };

  const handleDeleteCategory = (categoryId) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      setCategories(categories.filter(cat => cat.id !== categoryId));
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="onbr-modal-overlay" onClick={handleOverlayClick}>
      <div className="onbr-modal-content onbr-large" onClick={(e) => e.stopPropagation()}>
        <div className="onbr-modal-header">
          <h3>Allocation Items Management</h3>
          <button className="onbr-modal-close" onClick={onClose}>
            <MdClose size={24} />
          </button>
        </div>
        
        {/* Tabs for Items and Categories */}
        <div className="onbr-tabs-container">
          <div className="onbr-tabs">
            <button 
              className={`onbr-tab ${activeTab === 'items' ? 'active' : ''}`}
              onClick={() => setActiveTab('items')}
            >
              <MdComputer size={18} style={{ marginRight: '8px' }} />
              Items ({items.length})
            </button>
            <button 
              className={`onbr-tab ${activeTab === 'categories' ? 'active' : ''}`}
              onClick={() => setActiveTab('categories')}
            >
              <MdFolder size={18} style={{ marginRight: '8px' }} />
              Categories ({categories.length})
            </button>
          </div>
        </div>

        <div className="onbr-modal-body">
          {activeTab === 'items' ? (
            <>
              <form onSubmit={handleItemSubmit} className="onbr-allocation-form">
                <div className="onbr-form-group">
                  <input
                    type="text"
                    value={newItemName}
                    onChange={(e) => setNewItemName(e.target.value)}
                    placeholder="Enter item name (Laptop, Mobile, etc.)"
                    required
                  />
                </div>
                
                <div className="onbr-form-group">
                  <label>Select Category (Optional)</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="onbr-category-select"
                  >
                    <option value="">No Category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                
                <button 
                  type="submit" 
                  className="onbr-btn-primary"
                  disabled={!newItemName.trim()} // Disable if empty
                >
                  {editingItem ? (
                    <>
                      <MdEdit size={18} style={{ marginRight: '8px' }} />
                      Update Item
                    </>
                  ) : (
                    <>
                      <MdAdd size={18} style={{ marginRight: '8px' }} />
                      Add Item
                    </>
                  )}
                </button>
              </form>

              <div className="onbr-items-list">
                <h4>Allocatable Items</h4>
                {items.length === 0 ? (
                  <div className="onbr-empty-state">
                    <MdComputer size={32} />
                    <p>No items added yet. Start by adding your first item.</p>
                  </div>
                ) : (
                  items.map(item => (
                    <div key={item._id} className="onbr-item-card">
                      <div className="onbr-item-icon">
                        <MdComputer size={24} />
                      </div>
                      <div className="onbr-item-info">
                        <span className="onbr-item-name">{item.name}</span>
                        {item.category && (
                          <span className="onbr-item-category">
                            <MdFolder size={12} style={{ marginRight: '4px' }} />
                            {item.category}
                          </span>
                        )}
                      </div>
                      <div className="onbr-item-actions">
                        <button
                          className="onbr-btn-edit"
                          onClick={() => {
                            setEditingItem(item);
                            setNewItemName(item.name);
                            setSelectedCategory(item.category || "");
                          }}
                        >
                          <MdEdit size={16} style={{ marginRight: '6px' }} />
                          Edit
                        </button>
                        <button
                          className="onbr-btn-danger"
                          onClick={() => onDelete(item._id)}
                        >
                          <MdDelete size={16} style={{ marginRight: '6px' }} />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              <form onSubmit={handleCategorySubmit} className="onbr-category-form">
                <div className="onbr-form-group">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Enter category name (Electronics, Furniture, etc.)"
                    required
                  />
                </div>
                <button type="submit" className="onbr-btn-primary">
                  <MdAdd size={18} style={{ marginRight: '8px' }} />
                  Add Category
                </button>
              </form>

              <div className="onbr-categories-list">
                <h4>Item Categories</h4>
                {categories.length === 0 ? (
                  <div className="onbr-empty-state">
                    <MdFolder size={32} />
                    <p>No categories created yet. Add your first category.</p>
                  </div>
                ) : (
                  categories.map(cat => (
                    <div key={cat.id} className="onbr-category-card">
                      <div className="onbr-category-icon">
                        <MdFolder size={24} />
                      </div>
                      <div className="onbr-category-info">
                        <span className="onbr-category-name">{cat.name}</span>
                        <span className="onbr-category-count">
                          {items.filter(item => item.category === cat.name).length} items
                        </span>
                      </div>
                      <div className="onbr-category-actions">
                        <button
                          className="onbr-btn-edit"
                          onClick={() => {
                            // Edit category functionality
                            const newName = prompt("Enter new category name:", cat.name);
                            if (newName && newName.trim()) {
                              setCategories(categories.map(c => 
                                c.id === cat.id ? { ...c, name: newName.trim() } : c
                              ));
                            }
                          }}
                        >
                          <MdEdit size={16} style={{ marginRight: '6px' }} />
                          Edit
                        </button>
                        <button
                          className="onbr-btn-danger"
                          onClick={() => handleDeleteCategory(cat.id)}
                        >
                          <MdDelete size={16} style={{ marginRight: '6px' }} />
                          Delete
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ReportingManagerModal = ({ isOpen, onClose, managers, employees, onAssignManager }) => {
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedManager, setSelectedManager] = useState("");

  const handleAssign = () => {
    if (selectedEmployee && selectedManager) {
      onAssignManager(selectedEmployee, selectedManager);
      setSelectedEmployee("");
      setSelectedManager("");
      onClose();
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="onbr-modal-overlay" onClick={handleOverlayClick}>
      <div className="onbr-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="onbr-modal-header">
          <h3>Assign Reporting Manager</h3>
          <button className="onbr-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="onbr-modal-body">
          <div className="onbr-form-group">
            <label>Select Employee</label>
            <select
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
            >
              <option value="">Choose Employee</option>
              {employees.map(emp => (
                <option key={emp._id} value={emp._id}>
                  {emp.name} - {emp.department}
                </option>
              ))}
            </select>
          </div>
          <div className="onbr-form-group">
            <label>Select Reporting Manager</label>
            <select
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value)}
            >
              <option value="">Choose Manager</option>
              {managers.map(mgr => (
                <option key={mgr._id} value={mgr._id}>
                  {mgr.name} - {mgr.role}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="onbr-modal-footer">
          <button className="onbr-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="onbr-btn-primary"
            onClick={handleAssign}
            disabled={!selectedEmployee || !selectedManager}
          >
            Assign Manager
          </button>
        </div>
      </div>
    </div>
  );
};

const BulkOperationsModal = ({ isOpen, onClose, onBulkImport }) => {
  const [importMethod, setImportMethod] = useState("csv");
  const [csvData, setCsvData] = useState("");
  const [jsonData, setJsonData] = useState("");
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFields, setSelectedFields] = useState([
    "emp_id", "name", "email", "mobile", "department", "role", "company_email", "reporting_manager", "allocated_item"
  ]);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalDetails, setUpgradeModalDetails] = useState({
    currentCount: 0,
    limit: 2,
    message: ''
  });
  const [employees, setEmployees] = useState([]);

  // Fetch current employees count
  useEffect(() => {
    const fetchEmployeeCount = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const companyName = sessionStorage.getItem("companyName");

        const res = await fetch(
          `${API_BASE_URL}/api/employees?company_name=${encodeURIComponent(companyName)}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            }
          }
        );

        if (res.ok) {
          const data = await res.json();
          let employeesData = [];

          if (Array.isArray(data)) {
            employeesData = data;
          } else if (data?.data && Array.isArray(data.data)) {
            employeesData = data.data;
          } else if (data?.employees && Array.isArray(data.employees)) {
            employeesData = data.employees;
          }

          setEmployees(employeesData);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };

    if (isOpen) {
      fetchEmployeeCount();
    }
  }, [isOpen]);

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;

      if (uploadedFile.type === "application/json") {
        try {
          const jsonContent = JSON.parse(content);
          setJsonData(JSON.stringify(jsonContent, null, 2));
          setPreviewData(Array.isArray(jsonContent) ? jsonContent.slice(0, 5) : [jsonContent]);
        } catch (error) {
          alert("Invalid JSON file");
        }
      } else {
        // CSV file
        setCsvData(content);
        parseCSV(content);
      }
    };

    reader.readAsText(uploadedFile);
  };

  const parseCSV = (csvText) => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    const preview = [];

    for (let i = 1; i < Math.min(6, lines.length); i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(',').map(v => v.trim());
        const obj = {};
        headers.forEach((header, index) => {
          obj[header] = values[index] || '';
        });
        preview.push(obj);
      }
    }

    setPreviewData(preview);
  };

  const handleImport = async () => {
    setIsLoading(true);

    try {
      let employeesData = [];

      if (importMethod === "csv" && csvData) {
        employeesData = parseCSVToArray(csvData);
      } else if (importMethod === "json" && jsonData) {
        employeesData = JSON.parse(jsonData);
      }

      if (employeesData.length === 0) {
        alert("⚠️ No data to import!");
        setIsLoading(false);
        return;
      }

      // =============== CHECK SUBSCRIPTION LIMITS ===============
      const { checkSubscriptionLimits } = await import('../utils/subscriptionUtils');
      const checkResult = checkSubscriptionLimits(employees.length);

      console.log('📊 Bulk import subscription check:', {
        currentEmployees: employees.length,
        toImport: employeesData.length,
        checkResult,
        subscription: JSON.parse(sessionStorage.getItem('subscription') || '{}')
      });

      if (!checkResult.allowed) {
        // Show upgrade modal
        setUpgradeModalDetails({
          currentCount: employees.length,
          limit: checkResult.limit,
          message: checkResult.reason + ` You're trying to import ${employeesData.length} employees.`
        });
        setShowUpgradeModal(true);
        setIsLoading(false);
        return;
      }

      // Check if import will exceed limit
      if (employees.length + employeesData.length > checkResult.limit) {
        setUpgradeModalDetails({
          currentCount: employees.length,
          limit: checkResult.limit,
          message: `Free tier is limited to ${checkResult.limit} employees. ` +
            `You have ${employees.length} employees and trying to import ${employeesData.length} more. ` +
            `Upgrade to Pro for unlimited employees.`
        });
        setShowUpgradeModal(true);
        setIsLoading(false);
        return;
      }
      // =============== END SUBSCRIPTION CHECK ===============

      const token = sessionStorage.getItem("token");
      const companyName = sessionStorage.getItem("companyName");

      console.log("📦 Sending bulk import request...");
      console.log("🏢 Company:", companyName);
      console.log("👥 Employees:", employeesData.length);

      const response = await fetch(`${API_BASE_URL}/api/employees/bulk-import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        body: JSON.stringify({
          employeesData: employeesData,
          company_name: companyName
        })
      });

      const result = await response.json();
      console.log("📊 Bulk import result:", result);

      if (result.success) {
        // Show success message
        let successMessage = `✅ Successfully imported ${result.summary.successful} employees!\n`;

        if (result.summary.departmentsAdded > 0) {
          successMessage += `✅ Auto-created ${result.summary.departmentsAdded} departments:\n`;
          result.details.departmentsAdded.forEach(dept => {
            successMessage += `   - ${dept}\n`;
          });
        }

        alert(successMessage);

        // IMPORTANT: Pass the result to parent component
        if (typeof onBulkImport === 'function') {
          onBulkImport(result);
        }

        // Close modal
        onClose();

        // Force refresh data after 1 second
        setTimeout(() => {
          alert("🔄 Departments list will be refreshed now!");
        }, 500);

      } else {
        alert(`❌ Import failed: ${result.message}`);
      }
    } catch (error) {
      console.error("Import error:", error);
      alert("❌ Error during import. Please check console.");
    } finally {
      setIsLoading(false);
    }
  };

  const parseCSVToArray = (csvText) => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

    console.log("📊 CSV Headers:", headers);

    return lines.slice(1)
      .filter(line => line.trim())
      .map((line, lineIndex) => {
        // Handle CSV with quotes properly
        const values = line.match(/(?:[^,"]+|"[^"]*")+/g) || [];
        const trimmedValues = values.map(v => v.replace(/^"|"$/g, '').trim());

        const obj = {};
        headers.forEach((header, index) => {
          if (header === 'allocated_items' || header === 'allocated_item') {
            // Keep allocated items as is, don't convert to lowercase
            obj[header] = trimmedValues[index] || '';
            console.log(`📝 Line ${lineIndex + 1}: ${header} = "${obj[header]}"`);
          } else {
            obj[header] = trimmedValues[index] || '';
          }
        });

        console.log(`📋 Parsed employee ${lineIndex + 1}:`, obj);
        return obj;
      });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleNavigateToUpgrade = () => {
    // Close the modal
    setShowUpgradeModal(false);
    onClose(); // Close bulk modal too

    // Store the current state in sessionStorage for after payment
    sessionStorage.setItem('upgradeFromOnboarding', 'true');

    // Navigate to pricing page
    const companyName = sessionStorage.getItem('companyName');
    const userEmail = sessionStorage.getItem('userEmail');

    // Create a hidden link to navigate
    const navLink = document.createElement('a');
    navLink.href = `/hr-pricing?from=onboarding&company=${encodeURIComponent(companyName)}&email=${encodeURIComponent(userEmail)}`;
    navLink.target = '_blank';
    navLink.click();
  };

  if (!isOpen) return null;

  return (
    <div className="onbr-modal-overlay" onClick={handleOverlayClick}>
      <div className="onbr-modal-content onbr-large" onClick={(e) => e.stopPropagation()}>
        <div className="onbr-modal-header">
          <h3>Bulk Employee Import</h3>
          <button className="onbr-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="onbr-modal-body">
          {/* Import Method Selection */}
          <div className="onbr-import-methods">
            <div className="onbr-method-tabs">
              <button
                className={`onbr-method-tab ${importMethod === 'csv' ? 'active' : ''}`}
                onClick={() => setImportMethod('csv')}
              >
                <FiEdit size={18} />  CSV Format
              </button>
              <button
                className={`onbr-method-tab ${importMethod === 'json' ? 'active' : ''}`}
                onClick={() => setImportMethod('json')}
              >
                <FiFile size={18} /> JSON Format
              </button>
              <button
                className="onbr-method-tab"
                onClick={() => document.getElementById('fileUpload').click()}
              >
                <FiFolder size={18} /> Upload File
              </button>
              <input
                id="fileUpload"
                type="file"
                accept=".csv,.json"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </div>
          </div>

          {/* Field Selection */}
          <div className="onbr-field-selection">
            <h4>Select Fields to Import:</h4>
            <div className="onbr-field-checkboxes">
              {[
                { id: 'emp_id', label: 'Employee ID', required: true },
                { id: 'name', label: 'Full Name', required: true },
                { id: 'email', label: 'Personal Email', required: true },
                { id: 'company_email', label: 'Company Email' },
                { id: 'mobile', label: 'Mobile Number', required: true },
                { id: 'department', label: 'Department', required: true },
                { id: 'role', label: 'Role', required: true },
                { id: 'reporting_manager', label: 'Reporting Manager' },
                { id: 'allocated_items', label: 'Allocated Item' }
              ].map(field => (
                <label key={field.id} className="onbr-field-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedFields.includes(field.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedFields([...selectedFields, field.id]);
                      } else {
                        if (!field.required) {
                          setSelectedFields(selectedFields.filter(f => f !== field.id));
                        }
                      }
                    }}
                    disabled={field.required}
                  />
                  <span className="onbr-checkbox-label">
                    {field.label}
                    {field.required && <span className="onbr-required-star">*</span>}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Format Guide */}
          <div className="onbr-bulk-import-guide">
            <h4><FiFile size={18} /> Format Guide:</h4>

            {importMethod === "csv" ? (
              <div className="onbr-format-example">
                <p><strong>CSV Format (comma-separated):</strong></p>
                <pre>
                  {`emp_id,name,email,mobile,department,role,company_email,reporting_manager,allocated_item
EMP001,John Doe,john@example.com,9876543210,Engineering,Developer,john.doe@company.com,Manager Name,Laptop
EMP002,Jane Smith,jane@example.com,9123456789,Marketing,Manager,jane.smith@company.com,Director Name,"Laptop, Mobile"
EMP003,Raj Kumar,raj@example.com,9988776655,Sales,Executive,raj.kumar@company.com,Sales Head,"Laptop, Headset"`}
                </pre>
              </div>
            ) : (
              <div className="onbr-format-example">
                <p><strong>JSON Format:</strong></p>
                <pre>
                  {`[
  {
    "emp_id": "EMP001",
    "name": "John Doe",
    "email": "john@example.com",
    "mobile": "9876543210",
    "department": "Engineering",
    "role": "Developer",
    "company_email": "john.doe@company.com",
    "reporting_manager": "Manager Name",
    "allocated_item": "Laptop"
  },
  {
    "emp_id": "EMP002",
    "name": "Jane Smith",
    "email": "jane@example.com",
    "mobile": "9123456789",
    "department": "Marketing",
    "role": "Manager",
    "company_email": "jane.smith@company.com",
    "reporting_manager": "Director Name",
    "allocated_item": "Laptop, Mobile"
  }
]`}
                </pre>
              </div>
            )}

            <div className="onbr-important-notes">
              <h5 className="onbr-notes-title">
  <FiInfo size={18} style={{ marginRight: "8px", verticalAlign: "middle" }} />
  Important Notes
</h5>

<ul className="onbr-notes-list">
  <li>
    <FiCheckCircle size={16} className="note-icon success" />
    <strong>Required fields:</strong> emp_id, name, email, mobile, department, role
  </li>

  <li>
    <FiCheckCircle size={16} className="note-icon success" />
    <strong>Employee ID:</strong> Must be unique for each employee
  </li>

  <li>
    <FiCheckCircle size={16} className="note-icon success" />
    <strong>Email:</strong> Must be unique for each employee
  </li>

  <li>
    <FiCheckCircle size={16} className="note-icon success" />
    <strong>Temporary password:</strong> Auto-generated and sent via email
  </li>

  <li>
    <FiCheckCircle size={16} className="note-icon success" />
    <strong>Company name:</strong> Automatically taken from your profile
  </li>

  <li>
    <FiAlertTriangle size={16} className="note-icon warning" />
    <strong>Free Tier Limit:</strong> Maximum {upgradeModalDetails.limit} employees
  </li>

  <li>
    <FiStar size={16} className="note-icon premium" />
    <strong>Pro Tier:</strong> Unlimited employees with bulk import
  </li>
</ul>

            </div>
          </div>

          {/* Data Input */}
          <div className="onbr-form-group">
            <label>
              {importMethod === "csv" ? "Paste CSV Data:" : "Paste JSON Data:"}
            </label>
            <textarea
              value={importMethod === "csv" ? csvData : jsonData}
              onChange={(e) => importMethod === "csv" ? setCsvData(e.target.value) : setJsonData(e.target.value)}
              placeholder={importMethod === "csv" ?
                "Paste CSV data here...\n\nFormat:\nemp_id,name,email,mobile,department,role,..." :
                "Paste JSON array here...\n\nFormat:\n[\n  {\n    \"emp_id\": \"...\",\n    \"name\": \"...\",\n    ...\n  }\n]"
              }
              rows="10"
              className="onbr-data-textarea"
            />
          </div>

          {/* Data Preview */}
          {previewData.length > 0 && (
            <div className="onbr-data-preview">
              <h4><MdVisibility size={18} /> Data Preview (First {previewData.length} records):</h4>
              <div className="onbr-preview-table-container">
                <table className="onbr-preview-table">
                  <thead>
                    <tr>
                      {Object.keys(previewData[0] || {}).map(key => (
                        <th key={key}>{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, index) => (
                      <tr key={index}>
                        {Object.values(row).map((value, idx) => (
                          <td key={idx}>{value}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="onbr-preview-note">
                Showing {previewData.length} of total records •
                Current Employees: {employees.length} •
                Free Limit: {upgradeModalDetails.limit}
              </p>
            </div>
          )}
        </div>

        <div className="onbr-modal-footer">
          <div className="onbr-modal-footer-left">
            <button
              className="onbr-btn-secondary"
              onClick={() => {
                setCsvData("");
                setJsonData("");
                setFile(null);
                setPreviewData([]);
              }}
            >
              Clear Data
            </button>
            <button
              className="onbr-btn-secondary"
              onClick={() => {
                // Download template with emp_id field
                const template = importMethod === "csv" ?
                  "emp_id,name,email,mobile,department,role,company_email,reporting_manager,allocated_item\nEMP001,John Doe,john@example.com,9876543210,Engineering,Developer,john.doe@company.com,Manager Name,Laptop" :
                  JSON.stringify([{
                    emp_id: "EMP001",
                    name: "John Doe",
                    email: "john@example.com",
                    mobile: "9876543210",
                    department: "Engineering",
                    role: "Developer",
                    company_email: "john.doe@company.com",
                    reporting_manager: "Manager Name",
                    allocated_item: "Laptop"
                  }], null, 2);

                const blob = new Blob([template], { type: importMethod === "csv" ? "text/csv" : "application/json" });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `employee_template.${importMethod === "csv" ? "csv" : "json"}`;
                a.click();
                URL.revokeObjectURL(url);
              }}
            >
              <FiDownload size={18} />Download Template
            </button>
          </div>

          <div className="onbr-modal-footer-right">
            <button className="onbr-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="onbr-btn-primary"
              onClick={handleImport}
              disabled={(!csvData && !jsonData && !file) || isLoading}
            >
              {isLoading ? (
                <>
                  <div className="onbr-spinner-small"></div>
                  Importing...
                </>
              ) : (
                `Import ${previewData.length > 0 ? previewData.length + ' Employees' : 'Employees'}`
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Upgrade Required Modal */}
      {showUpgradeModal && (
        <div className="onbr-modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="onbr-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="onbr-modal-header">
              <h3><FiSend size={18} /> Upgrade Required</h3>
              <button className="onbr-modal-close" onClick={() => setShowUpgradeModal(false)}>×</button>
            </div>

            <div className="onbr-modal-body">
              <div className="onbr-upgrade-alert">
                <div className="onbr-upgrade-icon-large"><FiLock size={18} /></div>
                <h4>Free Tier Limit Reached</h4>
                <p className="onbr-upgrade-message">
                  {upgradeModalDetails.message}
                </p>

                <div className="onbr-limit-stats">
                  <div className="onbr-stat-item">
                    <span className="onbr-stat-label">Current Employees:</span>
                    <span className="onbr-stat-value">{upgradeModalDetails.currentCount}</span>
                  </div>
                  <div className="onbr-stat-item">
                    <span className="onbr-stat-label">Free Tier Limit:</span>
                    <span className="onbr-stat-value">{upgradeModalDetails.limit}</span>
                  </div>
                  <div className="onbr-stat-item">
                    <span className="onbr-stat-label">Available Slots:</span>
                    <span className="onbr-stat-value">
                      {Math.max(0, upgradeModalDetails.limit - upgradeModalDetails.currentCount)}
                    </span>
                  </div>
                </div>

                <div className="onbr-plan-features">
                  <div className="onbr-feature-card">
                    <h5>Free Plan</h5>
                    <ul>
                      <li>✓ Up to 2 employees</li>
                      <li>✓ Basic employee management</li>
                      <li>✓ Department management</li>
                      <li>✗ No bulk import</li>
                      <li>✗ No advanced features</li>
                    </ul>
                  </div>
                  <div className="onbr-feature-card">
                    <h5>Pro Plan</h5>
                    <ul>
                      <li>✓ Unlimited employees</li>
                      <li>✓ Bulk import</li>
                      <li>✓ Advanced reporting</li>
                      <li>✓ Priority support</li>
                      <li>✓ Custom workflows</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="onbr-modal-footer">
              <button
                className="onbr-btn-secondary"
                onClick={() => setShowUpgradeModal(false)}
              >
                Cancel
              </button>
              <button
                className="onbr-btn-primary"
                onClick={handleNavigateToUpgrade}
              >
                Upgrade Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main OnboardingPage Component
const OnboardingPage = () => {
  const [formData, setFormData] = useState({
    emp_id: "",
    name: "",
    email: "",
    company_name: "",
    company_email: "",
    mobile: "",
    department: "",
    role: "",
    reporting_manager: "",
    allocated_items: [],
  });

  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("onboard");
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [allocateItems, setAllocateItems] = useState([]);
  const [managers, setManagers] = useState([]);
  const [user, setUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [editingId, setEditingId] = useState(null);

  // Add this state variable
const [allocatedEmployeesModal, setAllocatedEmployeesModal] = useState({
  isOpen: false,
  itemName: '',
  employees: []
});

  // Modal states
  const [quickViewModal, setQuickViewModal] = useState({ isOpen: false, employee: null });
  const [deptModal, setDeptModal] = useState(false);
  const [allocationModal, setAllocationModal] = useState(false);
  const [reportingModal, setReportingModal] = useState(false);
  const [bulkModal, setBulkModal] = useState(false);

  // Add these with your existing state declarations
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalDetails, setUpgradeModalDetails] = useState({
    currentCount: 0,
    limit: 2,
    message: ''
  });
  const [employeeCount, setEmployeeCount] = useState(0);

  const navigate = useNavigate();

  // Add these state variables at the top of your component
  const [scrollProgress, setScrollProgress] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const tableContainerRef = useRef(null);

  // Add these functions
  const handleTableScroll = () => {
    const container = tableContainerRef.current;
    if (container) {
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight - container.clientHeight;
      const progress = (scrollTop / scrollHeight) * 100;

      setScrollProgress(progress);
      setScrollPosition(scrollTop);
    }
  };

  // Function to get scroll bar color based on progress
  const getScrollColor = () => {
    if (scrollProgress < 25) {
      return '#4CAF50'; // Green for top
    } else if (scrollProgress < 50) {
      return '#2196F3'; // Blue for upper middle
    } else if (scrollProgress < 75) {
      return '#FF9800'; // Orange for lower middle
    } else {
      return '#F44336'; // Red for bottom
    }
  };

  // Function to get row color based on scroll position and index
  const getRowColor = (index) => {
    const container = tableContainerRef.current;
    if (!container) return 'transparent';

    const rowHeight = 60; // Approximate row height
    const visibleRows = Math.floor(container.clientHeight / rowHeight);
    const startIndex = Math.floor(scrollPosition / rowHeight);

    // Calculate distance from visible area center
    const distanceFromCenter = Math.abs(index - (startIndex + visibleRows / 2));

    // Create gradient effect based on distance from visible center
    if (distanceFromCenter < visibleRows / 4) {
      // Highlight center rows
      const intensity = 1 - (distanceFromCenter / (visibleRows / 4));
      return `rgba(33, 150, 243, ${intensity * 0.1})`; // Light blue highlight
    }

    return 'transparent';
  };

  // Initialize scroll event on component mount
  useEffect(() => {
    if (tableContainerRef.current) {
      handleTableScroll(); // Initialize progress
    }
  }, []);


  // Add this useEffect to check for subscription refresh when component loads
  useEffect(() => {
    const checkForUpgradeCompletion = async () => {
      const upgradedFromOnboarding = sessionStorage.getItem('upgradeFromOnboarding');

      if (upgradedFromOnboarding === 'true') {
        console.log('🔄 Checking if user just upgraded from onboarding...');

        // Clear the flag
        sessionStorage.removeItem('upgradeFromOnboarding');

        try {
          // Refresh subscription status
          const { refreshSubscription } = await import('../utils/subscriptionUtils');
          const updatedSubscription = await refreshSubscription();

          if (updatedSubscription?.isPaid) {
            console.log('✅ User has upgraded to paid plan!');
            setSuccess('✅ Plan upgraded successfully! You can now add more employees.');

            // Clear the modal if it's open
            setShowUpgradeModal(false);
          }
        } catch (error) {
          console.log('⚠️ Could not refresh subscription:', error);
        }
      }
    };

    checkForUpgradeCompletion();
  }, []);


  // Add this useEffect to check subscription status on load
  useEffect(() => {
    const checkSubscriptionOnLoad = async () => {
      try {
        const { refreshSubscription } = await import('../utils/subscriptionUtils');
        await refreshSubscription();

        console.log('📊 Initial subscription check completed');
      } catch (error) {
        console.log('⚠️ Could not refresh subscription on load:', error);
      }
    };

    checkSubscriptionOnLoad();
  }, []);

  // Load candidate data from sessionStorage
  useEffect(() => {
    const loadCandidateData = () => {
      const candidateData = sessionStorage.getItem('onboardingCandidate');

      if (candidateData) {
        try {
          const candidate = JSON.parse(candidateData);
          console.log('👤 Candidate loaded:', candidate);

          setFormData(prev => ({
            ...prev,
            name: candidate.name || '',
            email: candidate.email || '',
            company_email: candidate.company_email || candidate.email || '',
            mobile: candidate.mobile || candidate.phone || '',
            department: candidate.department || '',
            role: candidate.role || candidate.position || '',
            company_name: prev.company_name || sessionStorage.getItem('companyName') || '',
          }));

          setSuccess(`Auto-filled data for ${candidate.name}`);

          setTimeout(() => {
            console.log('🗑️ Clearing candidate data from session storage');
            sessionStorage.removeItem('onboardingCandidate');
          }, 5000);
        } catch (error) {
          console.error('❌ Error parsing candidate data:', error);
          setError('Failed to load candidate data.');
        }
      }
    };
    loadCandidateData();
  }, []);

  // Filter employees
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = employee.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.emp_id?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDepartment = selectedDepartment === 'all' || employee.department === selectedDepartment;
    return matchesSearch && matchesDepartment;
  });

  const fetchManagers = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const companyName = sessionStorage.getItem("companyName");

      console.log("👨‍💼 [FETCH MANAGERS] Company:", companyName);

      if (!companyName) {
        console.error("❌ No company name found");
        return;
      }

      const endpoints = [
        `${API_BASE_URL}/api/managers?company_name=${encodeURIComponent(companyName)}`,
        `${API_BASE_URL}/api/employees/managers?company_name=${encodeURIComponent(companyName)}`
      ];

      let foundManagers = [];

      for (const endpoint of endpoints) {
        try {
          console.log("🔍 Trying endpoint:", endpoint);
          const response = await fetch(endpoint, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Accept': 'application/json'
            }
          });

          if (response.ok) {
            const data = await response.json();
            console.log("📊 Response from", endpoint, ":", data);

            if (Array.isArray(data)) {
              foundManagers = data;
              break;
            } else if (data && Array.isArray(data.managers)) {
              foundManagers = data.managers;
              break;
            } else if (data && Array.isArray(data.data)) {
              foundManagers = data.data;
              break;
            }
          }
        } catch (apiErr) {
          console.log(`🚫 Endpoint ${endpoint} failed:`, apiErr.message);
        }
      }

      if (foundManagers.length === 0) {
        console.log("⚠️ No managers via API, using local filtering");
        foundManagers = filterManagersFromEmployees(employees);
      }

      console.log("✅ [FETCH MANAGERS] Final managers:", foundManagers.length);
      setManagers(foundManagers);

    } catch (err) {
      console.error("❌ [FETCH MANAGERS] Error:", err);
    }
  };

  const filterManagersFromEmployees = (employeesList) => {
    const managerRoles = [
      'Manager', 'Lead', 'Head', 'Director', 'Supervisor',
      'Chief', 'President', 'VP', 'SVP', 'CFO', 'CEO', 'CTO', 'COO'
    ];

    return employeesList
      .filter(emp => {
        if (!emp.role) return false;

        const roleLower = emp.role.toLowerCase();
        return managerRoles.some(managerRole =>
          roleLower.includes(managerRole.toLowerCase())
        );
      })
      .map(emp => ({
        _id: emp._id,
        name: emp.name,
        email: emp.email,
        role: emp.role,
        department: emp.department,
        emp_id: emp.emp_id,
        company_name: emp.company_name || sessionStorage.getItem("companyName")
      }));
  };

  useEffect(() => {
    fetchManagers();
  }, [employees]);

  useEffect(() => {
    const storedCompanyName = sessionStorage.getItem("companyName");
    if (storedCompanyName) {
      setFormData(prev => ({ ...prev, company_name: storedCompanyName }));
    }
  }, []);

  useEffect(() => {
    if (["employees", "departments", "allocate", "onboard", "reportManager"].includes(activeTab)) fetchEmployees();
    if (["departments", "allocate", "onboard", "reportManager"].includes(activeTab)) fetchDepartments();
    if (["allocate", "onboard"].includes(activeTab)) fetchAllocateItems();
  }, [activeTab]);

  // Load user
  useEffect(() => {
    const fetchUser = async () => {
      const token = sessionStorage.getItem("token");

      if (!token) {
        navigate("/");
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          setUser(data.user);
        } else {
          const fallbackUser = {
            name: sessionStorage.getItem('userName') || 'HR Manager',
            company: sessionStorage.getItem('companyName') || 'Company',
            email: sessionStorage.getItem('userEmail') || '',
            role: sessionStorage.getItem('userType') || 'hr'
          };

          if (fallbackUser.name && fallbackUser.company) {
            setUser(fallbackUser);
          } else {
            navigate("/");
          }
        }
      } catch (err) {
        const fallbackUser = {
          name: sessionStorage.getItem('userName'),
          company: sessionStorage.getItem('companyName'),
          email: sessionStorage.getItem('userEmail'),
          role: sessionStorage.getItem('userType')
        };

        if (fallbackUser.name && fallbackUser.company) {
          setUser(fallbackUser);
        } else {
          navigate("/");
        }
      }
    };

    fetchUser();
  }, [navigate]);

  const handleLogout = () => {
    sessionStorage.clear();
    navigate("/");
  };

  const handleChange = (e) => {
    const { name, value, type, checked, options } = e.target;

    if (name === "allocated_items") {
      const selectedValues = Array.from(options)
        .filter(option => option.selected)
        .map(option => option.value);
      setFormData(prev => ({
        ...prev,
        allocated_items: selectedValues
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Fetch functions
  const fetchEmployees = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const companyName = sessionStorage.getItem("companyName");

      console.log("👥 [FETCH EMPLOYEES] Fetching for company:", companyName);

      const res = await fetch(
        `${API_BASE_URL}/api/employees?company_name=${encodeURIComponent(companyName)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        }
      );

      if (!res.ok) {
        console.error("❌ Failed to fetch employees:", res.status);
        return;
      }

      const data = await res.json();
      console.log("📦 Employee API Response:", data);

      let employeesData = [];

      if (Array.isArray(data)) {
        employeesData = data;
      } else if (data?.data && Array.isArray(data.data)) {
        employeesData = data.data;
      } else if (data?.employees && Array.isArray(data.employees)) {
        employeesData = data.employees;
      } else {
        console.error("❌ Unexpected API response format:", data);
        return;
      }

      console.log(`✅ Found ${employeesData.length} employees`);

      const processedEmployees = employeesData.map(employee => {
        let allocatedItems = [];

        if (employee.allocated_items) {
          if (Array.isArray(employee.allocated_items)) {
            allocatedItems = employee.allocated_items
              .filter(item => item && typeof item === 'string')
              .map(item => item.trim());
          } else if (typeof employee.allocated_items === 'string') {
            allocatedItems = employee.allocated_items
              .split(',')
              .map(item => item.trim())
              .filter(item => item);
          }
        }

        if (allocatedItems.length === 0 && employee.allocated_item) {
          if (typeof employee.allocated_item === 'string') {
            allocatedItems = employee.allocated_item
              .split(',')
              .map(item => item.trim())
              .filter(item => item);
          }
        }

        console.log(`📦 Allocation for ${employee.name}:`, allocatedItems);

        return {
          ...employee,
          allocated_items: allocatedItems,
          allocated_item: allocatedItems.join(', '),
          name: employee.name || '',
          email: employee.email || '',
          mobile: employee.mobile || '',
          department: employee.department || '',
          role: employee.role || '',
          reporting_manager: employee.reporting_manager || '',
          emp_id: employee.emp_id || `EMP${employee._id ? employee._id.slice(-5) : '00000'}`
        };
      });

      processedEmployees.sort((a, b) => a.name.localeCompare(b.name));

      console.log(`✅ Processed ${processedEmployees.length} employees`);

      setEmployees(processedEmployees);

      fetchManagers();

    } catch (err) {
      console.error("❌ Error fetching employees:", err);
      setError("Failed to load employees. Please try again.");
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const companyName = sessionStorage.getItem("companyName");

      if (!companyName) {
        console.error("❌ Company name not found");
        return;
      }

      console.log("🔍 Fetching departments for:", companyName);

      const response = await fetch(
        `${API_BASE_URL}/api/departments?company_name=${encodeURIComponent(companyName)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        console.error(`❌ API Error: ${response.status}`);
        return;
      }

      const data = await response.json();
      console.log("📊 API Response:", data);

      let departmentsList = [];

      if (Array.isArray(data)) {
        departmentsList = data;
      } else if (data && Array.isArray(data.data)) {
        departmentsList = data.data;
      } else if (data && Array.isArray(data.departments)) {
        departmentsList = data.departments;
      }

      console.log(`✅ Found ${departmentsList.length} departments`);

      setDepartments(departmentsList);

    } catch (error) {
      console.error("❌ Fetch error:", error);
    }
  };

  const fetchAllocateItems = async () => {
    try {
      const token = sessionStorage.getItem("token");
      const companyName = sessionStorage.getItem("companyName");

      console.log("💼 [FETCH ITEMS] Fetching for company:", companyName);

      if (!companyName) {
        console.error("❌ No company name found");
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/allocate-items?company_name=${encodeURIComponent(companyName)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log("📡 [FETCH ITEMS] Response status:", response.status);

      if (!response.ok) {
        console.error("❌ [FETCH ITEMS] API Error:", response.status);
        return;
      }

      const data = await response.json();
      console.log("📊 [FETCH ITEMS] Response:", data);

      let itemsArray = [];

      if (Array.isArray(data)) {
        itemsArray = data;
      } else if (data && Array.isArray(data.data)) {
        itemsArray = data.data;
      } else if (data && Array.isArray(data.items)) {
        itemsArray = data.items;
      } else if (data && Array.isArray(data.allocateItems)) {
        itemsArray = data.allocateItems;
      }

      console.log("✅ [FETCH ITEMS] Found:", itemsArray.length, "items");
      setAllocateItems(itemsArray);

    } catch (err) {
      console.error("❌ [FETCH ITEMS] Error:", err);
    }
  };

  // UPDATED: handleSubmit with manual emp_id
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsSubmitting(true);

    try {
      const token = sessionStorage.getItem("token");
      const companyName = sessionStorage.getItem("companyName") || user?.company;

      // ====================== STEP 1: CHECK LIMIT FIRST ======================
      // Only check limit for NEW employees (not editing existing ones)
      if (!editingId) {
        // Use the subscription utility to check limits
        const checkResult = checkSubscriptionLimits(employees.length);

        console.log('📊 Onboarding check:', {
          employeesCount: employees.length,
          checkResult,
          subscription: JSON.parse(sessionStorage.getItem('subscription') || '{}')
        });

        if (!checkResult.allowed) {
          setUpgradeModalDetails({
            currentCount: checkResult.currentCount,
            limit: checkResult.limit,
            message: checkResult.reason
          });
          setShowUpgradeModal(true);
          setIsSubmitting(false);
          return; // STOP HERE - don't proceed with onboarding
        }
      }
      // ====================== END OF LIMIT CHECK ======================

      // Validate emp_id
      if (!formData.emp_id || formData.emp_id.trim() === "") {
        setError("Employee ID is required");
        setIsSubmitting(false);
        return;
      }

      // Prepare data with manual emp_id
      const submissionData = {
        emp_id: formData.emp_id.trim(), // Manual Employee ID
        name: formData.name,
        email: formData.email,
        company_name: formData.company_name,
        company_email: formData.company_email || formData.email,
        mobile: formData.mobile,
        department: formData.department,
        role: formData.role,
        reporting_manager: formData.reporting_manager || "",
        allocated_item: formData.allocated_items.join(", "),
        allocated_items: formData.allocated_items,
      };

      console.log("📤 Submitting employee data:", submissionData);

      let res, data;

      if (editingId) {
        res = await fetch(`${API_BASE_URL}/api/employees/${editingId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(submissionData),
        });
        data = await res.json();
        if (res.ok) {
          setSuccess("Employee updated successfully!");
          console.log("✅ Employee updated:", data);
        } else {
          setError(data.message || "Failed to update employee.");
        }
      } else {
        res = await fetch(`${API_BASE_URL}/api/employees/onboard`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(submissionData),
        });
        data = await res.json();
        if (res.status === 201) {
          setSuccess(`Employee onboarded! Emp ID: ${data.emp_id}`);
          console.log("✅ Employee onboarded:", data);
        } else {
          setError(data.message || "Failed to onboard employee.");
        }
      }

      // Reset form
      setFormData({
        emp_id: "",
        name: "",
        email: "",
        company_name: sessionStorage.getItem("companyName") || "",
        company_email: "",
        mobile: "",
        department: "",
        role: "",
        reporting_manager: "",
        allocated_items: [],
      });
      setEditingId(null);

      // Refresh employees list after a delay
      setTimeout(() => {
        fetchEmployees();
      }, 1000);

    } catch (err) {
      console.error("❌ Submission error:", err);
      setError("Server error. Please try again.");
    }
    finally {
      setIsSubmitting(false);
    }
  };

  // UPDATED: handleEdit to include emp_id
  const handleEdit = (employee) => {
    const allocatedItems = Array.isArray(employee.allocated_items)
      ? employee.allocated_items
      : employee.allocated_item
        ? [employee.allocated_item]
        : [];

    setFormData({
      emp_id: employee.emp_id || "", // Include emp_id
      name: employee.name,
      email: employee.email,
      company_name: employee.company_name || sessionStorage.getItem("companyName") || "",
      company_email: employee.company_email || "",
      mobile: employee.mobile,
      department: employee.department,
      role: employee.role,
      reporting_manager: employee.reporting_manager || "",
      allocated_items: allocatedItems,
    });
    setEditingId(employee._id);
    setActiveTab("onboard");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/employees/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setEmployees(employees.filter(e => e._id !== id));
        setSuccess("Employee deleted successfully!");
      }
    } catch (err) { setError("Error deleting employee"); }
  };

  // Modal handlers
  const handleAddDepartment = async (name) => {
    try {
      const token = sessionStorage.getItem("token");
      const companyName = sessionStorage.getItem("companyName");
      const res = await fetch(`${API_BASE_URL}/api/departments`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, company_name: companyName }),
      });
      const data = await res.json();
      if (res.ok) {
        setDepartments([...departments, data]);
        setSuccess("Department added successfully!");
      }
    } catch (err) { setError("Error adding department"); }
  };

  const handleUpdateDepartment = async (id, name) => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/departments/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (res.ok) {
        setDepartments(departments.map(d => d._id === id ? data : d));
        setSuccess("Department updated successfully!");
      }
    } catch (err) { setError("Error updating department"); }
  };

  const handleDeleteDepartment = async (id) => {
    if (!window.confirm("Are you sure you want to delete this department?")) return;
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/departments/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setDepartments(departments.filter(d => d._id !== id));
        setSuccess("Department deleted successfully!");
      }
    } catch (err) { setError("Error deleting department"); }
  };

  const handleAddAllocation = async (name) => {
    try {
      const token = sessionStorage.getItem("token");
      const company_name = sessionStorage.getItem("companyName");
      const res = await fetch(`${API_BASE_URL}/api/allocate-items`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, company_name }),
      });
      const data = await res.json();
      if (res.ok) {
        setAllocateItems([...allocateItems, data]);
        setSuccess("Item added successfully!");
      }
    } catch (err) { setError("Error adding item"); }
  };

  const handleUpdateAllocation = async (id, name) => {
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/allocate/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (res.ok) {
        setAllocateItems(allocateItems.map(i => i._id === id ? data : i));
        setSuccess("Item updated successfully!");
      }
    } catch (err) { setError("Error updating item"); }
  };

  const handleDeleteAllocation = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      const token = sessionStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/allocate/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setAllocateItems(allocateItems.filter(i => i._id !== id));
        setSuccess("Item deleted successfully!");
      }
    } catch (err) { setError("Error deleting item"); }
  };

  const handleAssignManager = async (employeeId, managerId) => {
    try {
      const token = sessionStorage.getItem("token");
      const employee = employees.find(emp => emp._id === employeeId);
      const manager = managers.find(mgr => mgr._id === managerId);

      const res = await fetch(`${API_BASE_URL}/api/employees/${employeeId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...employee, reporting_manager: manager.name }),
      });

      if (res.ok) {
        setSuccess(`Reporting manager assigned successfully!`);
        fetchEmployees();
      }
    } catch (err) { setError("Error assigning manager"); }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('profilePicture', file);

      const response = await fetch(`${API_BASE_URL}/api/user/upload-profile-picture`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        const newProfilePictureUrl = data.profilePicture.url;

        setUser(prev => ({
          ...prev,
          profilePicture: newProfilePictureUrl
        }));

        const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
        userData.profilePicture = newProfilePictureUrl;
        sessionStorage.setItem('userData', JSON.stringify(userData));
        sessionStorage.setItem('companyLogo', newProfilePictureUrl);

        alert('Profile picture updated successfully!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload profile picture');
    }
  };

  useEffect(() => {
    const fetchProfilePicture = async () => {
      try {
        const token = sessionStorage.getItem('token');
        if (!token) return;

        const res = await fetch(
          `${API_BASE_URL}/api/user/profile-picture-data-url`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        const data = await res.json();

        if (data.success && data.profilePicture) {
          setUser(prev => ({
            ...prev,
            profilePicture: data.profilePicture
          }));
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchProfilePicture();
  }, []);

  const handleBulkImport = async (importResult) => {
    console.log("🔄 Bulk import completed:", importResult);

    if (!importResult?.success) {
      console.error("❌ Import failed");
      return;
    }

    let message = `✅ Successfully imported ${importResult.summary?.successful || 0} employees`;
    if (importResult.summary?.departmentsAdded > 0) {
      message += `\n✅ Created ${importResult.summary.departmentsAdded} new departments`;
    }
    if (importResult.summary?.itemsAdded > 0) {
      message += `\n✅ Created ${importResult.summary.itemsAdded} new allocation items`;
    }
    setSuccess(message);

    setBulkModal(false);

    setTimeout(async () => {
      console.log("🔄 Step 1: Refreshing departments...");
      await fetchDepartments();

      console.log("🔄 Step 2: Refreshing employees...");
      await fetchEmployees();

      console.log("🔄 Step 3: Refreshing allocation items...");
      await fetchAllocateItems();

      console.log("🔄 Step 4: Refreshing managers...");
      await fetchManagers();

      console.log("📍 Switching to departments tab...");
      setActiveTab('departments');

      setTimeout(() => {
        setSuccess(
          `✅ Bulk import complete! ` +
          `Check Departments, Allocated Items, and Managers tabs for new data.`
        );
      }, 1000);

    }, 800);
  };

  if (!user) return (
    <div className="onbr-loading-container">
      <div className="onbr-loading-spinner"></div>
      <p>Loading your portal...</p>
    </div>
  );

  return (
    <div className="onbr-container">
      <header className="onbr-header-navigation">
        <div className="onbr-header-content">
          <div className="onbr-brand-identity">
            <div className="onbr-brand-text">
              <h1 className="onbr-company-title">{user?.company || 'Company'}</h1>
              <span className="onbr-portal-subtitle">Employee Onboarding</span>
            </div>
          </div>

          <div className="onbr-user-navigation">
            <button
              className="onbr-back-dashboard-btn"
              onClick={() => navigate('/dashboard')}
              title="Go back to HR Dashboard"
            >
              <span className="onbr-back-icon">←</span>
              Back to Dashboard
            </button>

            <div className="onbr-user-profile">
              <span className="onbr-user-greeting">Welcome, {user?.name || 'User'}</span>
              <span className="onbr-user-role">HR Manager</span>
            </div>

            <div
              className="onbr-user-avatar"
              onClick={(e) => {
                e.stopPropagation();
                const fileInput = document.createElement('input');
                fileInput.type = 'file';
                fileInput.accept = 'image/*';
                fileInput.onchange = handleProfilePictureUpload;
                fileInput.click();
              }}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                overflow: 'hidden',
                cursor: 'pointer',
                backgroundImage: user?.profilePicture ? `url(${user.profilePicture})` : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '15px',
                border: '2px solid #fff',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}
            >
              {!user?.profilePicture && (
                <span style={{ color: '#fff', fontWeight: 'bold', fontSize: '16px' }}>
                  {user?.name?.charAt(0) || 'U'}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="onbr-main-interface">
        <nav className="onbr-sidebar-navigation">
          <div className="onbr-sidebar-header">
            <h3 className="onbr-sidebar-title">Navigation</h3>
          </div>
          <ul className="onbr-navigation-menu">
            {[
              {
                id: 'onboard',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="8.5" cy="7" r="4"></circle>
                    <line x1="20" y1="8" x2="20" y2="14"></line>
                    <line x1="23" y1="11" x2="17" y2="11"></line>
                  </svg>
                ),
                text: 'Onboard Employee'
              },
              {
                id: 'employees',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                ),
                text: 'Employees'
              },
              {
                id: 'departments',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                    <line x1="9" y1="21" x2="9" y2="9"></line>
                    <circle cx="7" cy="6" r="1"></circle>
                    <circle cx="12" cy="6" r="1"></circle>
                    <circle cx="17" cy="6" r="1"></circle>
                  </svg>
                ),
                text: 'Departments'
              },
              {
                id: 'allocate',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                    <line x1="9" y1="10" x2="15" y2="10"></line>
                    <line x1="12" y1="7" x2="12" y2="13"></line>
                  </svg>
                ),
                text: 'Allocated Items'
              },
              {
                id: 'reportManager',
                icon: (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="8.5" cy="7" r="4"></circle>
                    <polyline points="17 11 19 13 23 9"></polyline>
                    <line x1="20" y1="4" x2="20" y2="8"></line>
                    <line x1="23" y1="6" x2="17" y2="6"></line>
                  </svg>
                ),
                text: 'Report Managers'
              }
            ].map(tab => (
              <li
                key={tab.id}
                className={`onbr-nav-item ${activeTab === tab.id ? 'onbr-nav-active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="onbr-nav-content">
                  <span className="onbr-nav-icon">{tab.icon}</span>
                  <span className="onbr-nav-text">{tab.text}</span>
                  {/* <div className="onbr-nav-indicator"></div> */}
                </div>

              </li>

            ))
            }<div className="onbr-quick-actions">
              <h4>Quick Actions</h4>
              <button className="onbr-quick-btn" onClick={() => setDeptModal(true)}>
                <span className="onbr-quick-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                    <line x1="9" y1="21" x2="9" y2="9"></line>
                    <line x1="14" y1="13" x2="14" y2="17"></line>
                    <line x1="17" y1="13" x2="17" y2="17"></line>
                  </svg>
                </span>
                Manage Departments
              </button>
              <button className="onbr-quick-btn" onClick={() => setAllocationModal(true)}>
                <span className="onbr-quick-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                    <circle cx="12" cy="12" r="1"></circle>
                    <line x1="12" y1="9" x2="12" y2="15"></line>
                  </svg>
                </span>
                Manage Items
              </button>
              <button className="onbr-quick-btn" onClick={() => setReportingModal(true)}>
                <span className="onbr-quick-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="8.5" cy="7" r="4"></circle>
                    <polyline points="17 11 19 13 23 9"></polyline>
                    <path d="M20 4v4"></path>
                    <path d="M18 6h4"></path>
                  </svg>
                </span>
                Assign Managers
              </button>
              <button className="onbr-quick-btn" onClick={() => setBulkModal(true)}>
                <span className="onbr-quick-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                    <line x1="12" y1="22.08" x2="12" y2="12"></line>
                    <line x1="8" y1="2" x2="8" y2="4"></line>
                    <line x1="16" y1="2" x2="16" y2="4"></line>
                  </svg>
                </span>
                Bulk Import
              </button>
            </div>
          </ul>
        </nav>

        <main className="onbr-content-area">
          <div className="onbr-content-wrapper">
            {success && (
              <div className="onbr-success-message">
                <span className="onbr-success-icon">✅</span>
                {success}
              </div>
            )}
            {error && (
              <div className="onbr-error-message">
                <span className="onbr-error-icon">❌</span>
                {error}
              </div>
            )}

            {/* UPDATED: Onboard Form with emp_id field */}
            {activeTab === 'onboard' && (
              <div className="onf-container">
                <div className="onf-header">
                  <h2 className="onf-gradient-text">{editingId ? 'Edit Employee' : 'Onboard New Employee'}</h2>
                  <div className="onf-header-decoration"></div>
                </div>
                <form onSubmit={handleSubmit} className="onf-form">
                  <div className="onf-grid">
                    {/* Employee ID Field - ADDED */}
                    <div className="onf-input-group">
                      <input
                        placeholder=" "
                        name="emp_id"
                        value={formData.emp_id}
                        onChange={handleChange}
                        required
                        disabled={!!editingId}
                      />
                      <label>Employee ID *</label>
                      <div className="onf-input-underline"></div>
                      <small className="onf-field-hint">
                        {editingId ? "Employee ID cannot be changed" : "Enter unique Employee ID (e.g., EMP001)"}
                      </small>
                    </div>

                    <div className="onf-input-group">
                      <input placeholder=" " name="name" value={formData.name} onChange={handleChange} required />
                      <label>Full Name *</label>
                      <div className="onf-input-underline"></div>
                    </div>

                    <div className="onf-input-group">
                      <input placeholder=" " name="email" type="email" value={formData.email} onChange={handleChange} required />
                      <label>Email *</label>
                      <div className="onf-input-underline"></div>
                    </div>

                    <div className="onf-input-group">
                      <input placeholder=" " name="company_name" value={formData.company_name} disabled />
                      <label>Company Name</label>
                      <div className="onf-input-underline"></div>
                    </div>

                    <div className="onf-input-group">
                      <input placeholder=" " name="company_email" type="email" value={formData.company_email} onChange={handleChange} />
                      <label>Company Email</label>
                      <div className="onf-input-underline"></div>
                    </div>

                    <div className="onf-input-group">
                      <input placeholder=" " name="mobile" value={formData.mobile} onChange={handleChange} required />
                      <label>Mobile *</label>
                      <div className="onf-input-underline"></div>
                    </div>

                    <div className="onf-input-group">
                      <select name="department" value={formData.department} onChange={handleChange} required>
                        <option value=""></option>
                        {departments.map(d => <option key={d._id} value={d.name}>{d.name}</option>)}
                      </select>
                      <label>Department *</label>
                      <div className="onf-input-underline"></div>
                    </div>

                    <div className="onf-input-group">
                      <input placeholder=" " name="role" value={formData.role} onChange={handleChange} required />
                      <label>Role *</label>
                      <div className="onf-input-underline"></div>
                    </div>

                    <div className="onf-input-group">
                      <select name="reporting_manager" value={formData.reporting_manager} onChange={handleChange}>
                        <option value=""></option>
                        {managers.map(manager => (
                          <option key={manager._id} value={manager.name}>{manager.name}</option>
                        ))}
                      </select>
                      <label>Reporting Manager</label>
                      <div className="onf-input-underline"></div>
                    </div>

                    <div className="onf-input-group">
                      <select
                        name="allocated_items"
                        value={formData.allocated_items}
                        onChange={handleChange}
                        multiple
                        className="onf-multi-select"
                      >
                        {allocateItems.map(i => (
                          <option key={i._id} value={i.name}>
                            {i.name}
                          </option>
                        ))}
                      </select>
                      <label>Allocated Items (Hold Ctrl/Cmd to select multiple)</label>
                      <div className="onf-input-underline"></div>
                      <div className="onf-selected-items">
                        {formData.allocated_items.map((item, index) => (
                          <span key={index} className="onf-selected-tag">
                            {item}
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  allocated_items: prev.allocated_items.filter((_, i) => i !== index)
                                }));
                              }}
                              className="onf-remove-tag"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <button type="submit" className="onf-submit-btn" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <div className="onf-spinner"></div>
                    ) : editingId ? (
                      'Update Employee'
                    ) : (
                      'Onboard Employee'
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* Upgrade Required Modal */}
            {showUpgradeModal && (
              <div className="onbr-modal-overlay" onClick={() => setShowUpgradeModal(false)}>
                <div className="onbr-modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="onbr-modal-header">
                    <h3><FiSend size={18} /> Upgrade Required</h3>
                    <button className="onbr-modal-close" onClick={() => setShowUpgradeModal(false)}>×</button>
                  </div>

                  <div className="onbr-modal-body">
                    <div className="onbr-upgrade-alert">
                      <div className="onbr-upgrade-icon-large"><FiLock size={18} /></div>
                      <h4>Free Tier Limit Reached</h4>
                      <p className="onbr-upgrade-message">
                        {upgradeModalDetails.message}
                      </p>

                      <div className="onbr-limit-stats">
                        <div className="onbr-stat-item">
                          <span className="onbr-stat-label">Current Employees:</span>
                          <span className="onbr-stat-value">{upgradeModalDetails.currentCount}</span>
                        </div>
                        <div className="onbr-stat-item">
                          <span className="onbr-stat-label">Free Tier Limit:</span>
                          <span className="onbr-stat-value">{upgradeModalDetails.limit}</span>
                        </div>
                        <div className="onbr-stat-item">
                          <span className="onbr-stat-label">Available Slots:</span>
                          <span className="onbr-stat-value">
                            {Math.max(0, upgradeModalDetails.limit - upgradeModalDetails.currentCount)}
                          </span>
                        </div>
                      </div>

                      <div className="onbr-plan-features">
                        <div className="onbr-feature-card">
                          <h5>Free Plan</h5>
                          <ul>
                            <li>✓ Up to 2 employees</li>
                            <li>✓ Basic employee management</li>
                            <li>✓ Department management</li>
                            <li>✗ No bulk import</li>
                            <li>✗ No advanced features</li>
                          </ul>
                        </div>
                        <div className="onbr-feature-card">
                          <h5>Pro Plan</h5>
                          <ul>
                            <li>✓ Unlimited employees</li>
                            <li>✓ Bulk import</li>
                            <li>✓ Advanced reporting</li>
                            <li>✓ Priority support</li>
                            <li>✓ Custom workflows</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="onbr-modal-footer">
                    <button
                      className="onbr-btn-secondary"
                      onClick={() => setShowUpgradeModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="onbr-btn-primary"
                      onClick={() => {
                        // Close the modal
                        setShowUpgradeModal(false);

                        // Store the current state in sessionStorage for after payment
                        sessionStorage.setItem('upgradeFromOnboarding', 'true');
                        sessionStorage.setItem('upgradeEmployeeCount', employees.length.toString());

                        // Navigate to pricing page with current subscription info
                        const currentSubscription = JSON.parse(sessionStorage.getItem('subscription') || '{}');

                        navigate('/hr-pricing', {
                          state: {
                            userEmail: user?.email || sessionStorage.getItem('userEmail'),
                            companyName: user?.company || sessionStorage.getItem('companyName'),
                            fromOnboarding: true,
                            currentSubscription: currentSubscription
                            // REMOVED: onSuccess callback - functions cannot be serialized
                          }
                        });
                      }}
                    >
                      Upgrade Now
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Employees tab */}
            {activeTab === 'employees' && (
              <div className="onbr-table-section">
                <div className="onbr-page-header-section">
                  <h2 className="onbr-page-title">Employee Directory</h2>
                  <p className="onbr-page-subtitle">
                    Total: {employees.length} employees •
                    Showing: {filteredEmployees.length} filtered
                  </p>
                </div>

                {/* Scroll Progress Indicator */}
                <div className="onbr-scroll-progress-container">
                  <div
                    className="onbr-scroll-progress-bar"
                    style={{ width: `${scrollProgress}%`, backgroundColor: getScrollColor() }}
                  ></div>
                </div>

                <div className="onbr-table-controls">
                  <div className="onbr-search-container">
                    <span className="onbr-search-icon"></span>
                    <input
                      type="text"
                      placeholder="Search by Employee ID, name, email, or role..."
                      className="onbr-search-input"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <select
                    className="onbr-filter-select"
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                  >
                    <option value="all">All Departments</option>
                    {departments.map(dept => (
                      <option key={dept._id} value={dept.name}>{dept.name}</option>
                    ))}
                  </select>
                  <button className="onbr-refresh-btn" onClick={fetchEmployees}>
                    <span className="onbr-refresh-icon"></span>
                    Refresh
                  </button>
                </div>

                <div
                  className="onbr-table-wrapper"
                  ref={tableContainerRef}
                  onScroll={handleTableScroll}
                >
                  {filteredEmployees.length > 0 ? (
                    <table className="onbr-data-table">
                      <thead>
                        <tr>
                          <th>Employee ID</th>
                          <th>Employee</th>
                          <th>Contact</th>
                          <th>Department</th>
                          <th>Role</th>
                          <th>Manager</th>
                          <th>Allocated Items</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEmployees.map((emp, index) => {
                          const allocatedItems = emp.allocated_items || [];
                          const managerName = emp.reporting_manager || (emp.manager || '—');

                          // Add row color based on scroll position and index
                          const rowColor = getRowColor(index);

                          return (
                            <tr
                              key={emp._id}
                              className="onbr-table-row"
                              style={{
                                backgroundColor: rowColor,
                                transition: 'background-color 0.3s ease'
                              }}
                            >
                              <td>
                                <span className="onbr-employee-id-cell">
                                  {emp.emp_id}
                                </span>
                              </td>
                              <td>
                                <div className="onbr-employee-cell">
                                  <div className="onbr-employee-avatar">
                                    {emp.name?.charAt(0).toUpperCase() || 'E'}
                                  </div>
                                  <div className="onbr-employee-info">
                                    <span className="onbr-employee-name">{emp.name}</span>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div className="onbr-contact-info">
                                  <span className="onbr-contact-email">{emp.email}</span>
                                  {emp.mobile && (
                                    <span className="onbr-contact-phone">{emp.mobile}</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <span className="onbr-department-tag">{emp.department}</span>
                              </td>
                              <td>{emp.role}</td>
                              <td>
                                <span className="onbr-manager-badge">
                                  {managerName}
                                </span>
                              </td>
                              <td>
                                <div className="onbr-allocated-items-cell">
                                  {allocatedItems.length > 0 ? (
                                    <div className="onbr-items-tags-small">
                                      {allocatedItems.map((item, index) => (
                                        <span key={index} className="onbr-item-tag-small">
                                          {item}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="onbr-no-items-text">—</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="onbr-action-buttons">
                                  <button
                                    className="onbr-table-btn onbr-view-table-btn"
                                    onClick={() => setQuickViewModal({ isOpen: true, employee: emp })}
                                    title="Quick View"
                                  >
                                    <MdVisibility size={18} /> 
                                  </button>
                                  <button
                                    className="onbr-table-btn onbr-edit-table-btn"
                                    onClick={() => handleEdit(emp)}
                                    title="Edit Employee"
                                  >
                                    <MdEdit size={18} /> 
                                  </button>
                                  <button
                                    className="onbr-table-btn onbr-delete-table-btn"
                                    onClick={() => handleDelete(emp._id)}
                                    title="Delete Employee"
                                  >
                                    <MdDelete size={18} /> 
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="onbr-empty-state">
                      <div className="onbr-empty-icon"><FiUsers size={18} /></div>
                      <h3 className="onbr-empty-title">
                        {employees.length === 0 ? 'No Employees Yet' : 'No Matching Employees'}
                      </h3>
                      <p className="onbr-empty-text">
                        {employees.length === 0
                          ? 'Get started by onboarding your first employee'
                          : 'Try changing your search or filter criteria'}
                      </p>
                      <button
                        className="onbr-primary-action-btn"
                        onClick={() => setActiveTab('onboard')}
                      >
                        {employees.length === 0 ? 'Onboard First Employee' : 'Add New Employee'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Departments tab */}
            {activeTab === 'departments' && (
              <div className="onbr-management-section">
                <div className="onbr-page-header-section">
                  <h2 className="onbr-page-title">Department Management</h2>
                  <p className="onbr-page-subtitle">Organize and manage company departments</p>
                  <div className="onbr-action-buttons-row">
                    <button className="onbr-primary-action-btn" onClick={() => setDeptModal(true)}>
                      <FiHome size={18} /> Manage Departments
                    </button>
                    <button
                      className="onbr-secondary-action-btn"
                      onClick={fetchDepartments}
                      title="Refresh departments list"
                    >
                      <MdRefresh size={18} /> Refresh
                    </button>
                  </div>
                </div>

                <div className="onbr-cards-grid">
                  {departments.length === 0 ? (
                    <div className="onbr-empty-state">
                      <div className="onbr-empty-icon"><FiHome size={18} /></div>
                      <h3>No Departments Found</h3>
                      <p>Create your first department or bulk import employees</p>
                    </div>
                  ) : (
                    departments.map(d => (
                      <div key={d._id} className="onbr-management-card">
                        <div className="onbr-card-icon"><FiHome size={18} /></div>
                        <h4>{d.name}</h4>
                        <p>Department</p>
                        <span className="onbr-card-meta">
                          Employees: {employees.filter(emp => emp.department === d.name).length}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

{activeTab === 'allocate' && (
  <div className="onbr-management-section">
    <div className="onbr-page-header-section">
      <h2 className="onbr-page-title">Allocated Items Management</h2>
      <p className="onbr-page-subtitle">
        {allocateItems.length} items available for allocation
      </p>

      <div className="onbr-action-buttons-row">
        <button
          className="onbr-primary-action-btn"
          onClick={() => setAllocationModal(true)}
        >
          <span className="onbr-btn-icon"><MdWork size={18} /></span>
          Add New Item
        </button>

        <button
          className="onbr-secondary-action-btn"
          onClick={() => {
            fetchAllocateItems();
            setSuccess("Allocation items refreshed!");
          }}
        >
          <span className="onbr-btn-icon"><MdRefresh size={18} /></span>
          Refresh Items
        </button>
      </div>
    </div>

    <div className="onbr-cards-grid">
      {allocateItems.length === 0 ? (
        <div className="onbr-empty-state">
          <div className="onbr-empty-icon"><MdWork size={18} /></div>
          <h3>No Allocation Items Found</h3>
          <p>Create your first allocation item or bulk import employees</p>
          <div className="onbr-empty-actions">
            <button
              className="onbr-primary-action-btn"
              onClick={() => setAllocationModal(true)}
            >
              Create First Item
            </button>
            <button
              className="onbr-secondary-action-btn"
              onClick={() => setBulkModal(true)}
            >
              Bulk Import Employees
            </button>
          </div>
        </div>
      ) : (
        allocateItems.map((item, index) => {
          const allocatedEmployees = employees.filter(emp =>
            Array.isArray(emp.allocated_items)
              ? emp.allocated_items.includes(item.name)
              : emp.allocated_item === item.name
          );
          const allocatedCount = allocatedEmployees.length;

          return (
            <div key={item._id || index} className="onbr-management-card">
              <div className="onbr-card-header">
                <div className="onbr-card-icon"><MdWork size={18} /></div>
                <div className="onbr-card-title">
                  <h4>{item.name}</h4>
                  <span className="onbr-card-subtitle">
                    {allocatedCount} employees allocated
                  </span>
                </div>
              </div>

              <div className="onbr-card-content">
                <div className="onbr-card-meta">
                  <span><MdCalendarToday size={18} /> Created: {new Date(item.createdAt || Date.now()).toLocaleDateString()}</span>
                </div>

                {allocatedCount > 0 && (
                  <div className="onbr-allocated-to">
                    <small>Allocated to:</small>
                    <div className="onbr-employee-chips">
                      {allocatedEmployees
                        .slice(0, 3)
                        .map(emp => (
                          <span key={emp._id} className="onbr-employee-chip">
                            {emp.name.split(' ')[0]}
                          </span>
                        ))}
                      {allocatedCount > 3 && (
                        <span 
                          className="onbr-employee-chip-more"
                          onClick={() => setAllocatedEmployeesModal({
                            isOpen: true,
                            itemName: item.name,
                            employees: allocatedEmployees
                          })}
                          style={{ cursor: 'pointer' }}
                        >
                          +{allocatedCount - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>

    {/* Allocated Employees Modal */}
    {allocatedEmployeesModal.isOpen && (
      <div className="onbr-modal-overlay" onClick={() => setAllocatedEmployeesModal({ isOpen: false, itemName: '', employees: [] })}>
        <div className="onbr-modal" onClick={(e) => e.stopPropagation()}>
          <div className="onbr-modal-header">
            <h3>Employees Allocated to: {allocatedEmployeesModal.itemName}</h3>
            <button 
              className="onbr-modal-close"
              onClick={() => setAllocatedEmployeesModal({ isOpen: false, itemName: '', employees: [] })}
            >
              ✕
            </button>
          </div>
          <div className="onbr-modal-body">
            <div className="onbr-employee-list">
              {allocatedEmployeesModal.employees.length > 0 ? (
                <table className="onbr-employee-list-table">
                  <thead>
                    <tr>
                      <th>Employee ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Department</th>
                      <th>Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocatedEmployeesModal.employees.map(emp => (
                      <tr key={emp._id}>
                        <td>{emp.emp_id}</td>
                        <td>
                          <div className="onbr-employee-list-name">
                            <div className="onbr-employee-list-avatar">
                              {emp.name?.charAt(0).toUpperCase() || 'E'}
                            </div>
                            <span>{emp.name}</span>
                          </div>
                        </td>
                        <td>{emp.email}</td>
                        <td>
                          <span className="onbr-dept-badge">{emp.department}</span>
                        </td>
                        <td>{emp.role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="onbr-empty-list">
                  <p>No employees allocated to this item.</p>
                </div>
              )}
            </div>
          </div>
          <div className="onbr-modal-footer">
            <button 
              className="onbr-primary-action-btn"
              onClick={() => setAllocatedEmployeesModal({ isOpen: false, itemName: '', employees: [] })}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    )}
  </div>
)}

            {activeTab === 'reportManager' && (
              <div className="onbr-report-manager-section">
                <ReportManager
                  employees={employees}
                  departments={departments}
                  managers={managers}
                  onRefreshManagers={fetchManagers}
                  companyName={user?.company || sessionStorage.getItem("companyName")}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modal Components */}
      <EmployeeQuickViewModal
        employee={quickViewModal.employee}
        isOpen={quickViewModal.isOpen}
        onClose={() => setQuickViewModal({ isOpen: false, employee: null })}
      />

      <DepartmentManagementModal
        isOpen={deptModal}
        onClose={() => setDeptModal(false)}
        departments={departments}
        onAdd={handleAddDepartment}
        onEdit={handleUpdateDepartment}
        onDelete={handleDeleteDepartment}
      />

      <AllocationManagementModal
        isOpen={allocationModal}
        onClose={() => setAllocationModal(false)}
        items={allocateItems}
        onAdd={handleAddAllocation}
        onEdit={handleUpdateAllocation}
        onDelete={handleDeleteAllocation}
      />

      <ReportingManagerModal
        isOpen={reportingModal}
        onClose={() => setReportingModal(false)}
        managers={managers}
        employees={employees}
        onAssignManager={handleAssignManager}
      />

      <BulkOperationsModal
        isOpen={bulkModal}
        onClose={() => setBulkModal(false)}
        onBulkImport={handleBulkImport}
      />
    </div>
  );
};

export default OnboardingPage;